import { create } from 'zustand';
import { AxiosError } from 'axios';
import { HSE_TERMS, type HseTermKey } from '../constants/hseTerms';
import { getCollectionSites } from '../services/collection-site.service';
import {
  addHseEvidence,
  getHseWeeklyHistory,
  getHseWeeklyReport,
  listHseEvidence,
  listHseWeeks,
  removeHseEvidence,
  reopenHseWeeklyReport,
  saveHseWeeklyReport,
  submitHseWeeklyReport,
} from '../services/hse-weekly-report.service';
import {
  generatePresignedUploadUrl,
  getAllowedExtension,
  parseUtilityProofPresignedResponse,
  uploadFileToPresignedUrl,
} from '../services/utility-proof.service';
import { CollectionSiteSchema } from '../types/collection-site.type';
import type {
  HseEvidenceFile,
  HseHistoryEvent,
  HseReportStatus,
  HseWeekExposureDraft,
  HseWeekSummary,
  HseWeeklyDraft,
  HseWeeklyReport,
  HseWeeklyRow,
} from '../types/hse-weekly-report.type';
import { defaultWeekStartIso } from '../utils/hseWeek.util';
import { draftFromReport, emptyDraft, emptyExposure, exposureFromReport } from '../utils/hseWeeklyStorage.util';

const STORAGE_KEY = 'hseWeeklyPreferences';
const NOTES_MAX = 2000;

interface GenericResponse<T> {
  error?: { message?: string; code?: string } | null;
  response?: { message?: string; code?: string } | null;
  result: T;
}

/** A message for the page to show: an i18n key (+ params), or server text as-is. */
export type HseNotice = {
  tone: 'success' | 'error';
  key?: string;
  params?: Record<string, string | number>;
  text?: string;
};

function sortSites(sites: CollectionSiteSchema[]): CollectionSiteSchema[] {
  return [...sites].sort((a, b) => a.id.localeCompare(b.id));
}

function loadStoredPreferences(): { selectedSiteId: string | null; selectedWeekStart: string | null } {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { selectedSiteId: null, selectedWeekStart: null };
    const parsed = JSON.parse(raw) as { selectedSiteId?: string; selectedWeekStart?: string };
    return {
      selectedSiteId: parsed.selectedSiteId ?? null,
      selectedWeekStart: parsed.selectedWeekStart ?? null,
    };
  } catch {
    return { selectedSiteId: null, selectedWeekStart: null };
  }
}

function parseWholeNumber(s: string): { value?: number; invalid: boolean } {
  const t = s.trim();
  if (t === '') return { value: 0, invalid: false };
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return { invalid: true };
  return { value: n, invalid: false };
}

/** Empty means "not reported" (null), unlike counts where empty means zero. */
function parseOptionalNumber(s: string, { integer }: { integer: boolean }): { value: number | null; invalid: boolean } {
  const t = s.trim();
  if (t === '') return { value: null, invalid: false };
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || (integer && !Number.isInteger(n))) return { value: null, invalid: true };
  return { value: n, invalid: false };
}

function serverText(error: unknown): string | undefined {
  if (error instanceof AxiosError) {
    const data = error.response?.data as
      | { detail?: unknown; error?: { message?: string }; response?: { message?: string } }
      | undefined;
    if (typeof data?.detail === 'string') return data.detail;
    return data?.error?.message || data?.response?.message || undefined;
  }
  return undefined;
}

function failure(error: unknown, key: string): HseNotice {
  const text = serverText(error);
  return text ? { tone: 'error', text } : { tone: 'error', key };
}

interface HseWeeklyState {
  sites: CollectionSiteSchema[];
  selectedSiteId: string | null;
  selectedWeekStart: string;
  draft: HseWeeklyDraft;
  exposure: HseWeekExposureDraft;
  report: HseWeeklyReport | null;
  evidence: HseEvidenceFile[];
  history: HseHistoryEvent[];
  weeks: HseWeekSummary[];
  organizationId: string | null;
  isLoadingSites: boolean;
  isLoadingReport: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  isUploadingEvidence: boolean;
  isLoadingHistory: boolean;
  isLoadingWeeks: boolean;
  notice: HseNotice | null;
  clearNotice: () => void;
  fetchSites: (organizationId: string) => Promise<void>;
  setSelectedSiteId: (siteId: string | null) => void;
  setSelectedWeekStart: (weekStart: string) => void;
  updateDraftRow: (termKey: HseTermKey, field: 'notes' | 'weeklyCount', value: string) => void;
  updateExposure: (field: keyof HseWeekExposureDraft, value: string) => void;
  loadReport: () => Promise<void>;
  saveReport: () => Promise<boolean>;
  submitWeek: () => Promise<boolean>;
  reopenWeek: (reason: string) => Promise<boolean>;
  loadEvidence: () => Promise<void>;
  uploadEvidence: (files: File[], termKey: HseTermKey | null) => Promise<boolean>;
  removeEvidence: (evidenceId: string) => Promise<boolean>;
  loadHistory: () => Promise<void>;
  loadWeeks: () => Promise<void>;
  loadPreferences: () => void;
  savePreferences: () => void;
  resetForOrganizationChange: () => void;
}

export const useHseWeeklyStore = create<HseWeeklyState>((set, get) => ({
  sites: [],
  selectedSiteId: null,
  selectedWeekStart: defaultWeekStartIso(),
  draft: emptyDraft(),
  exposure: emptyExposure(),
  report: null,
  evidence: [],
  history: [],
  weeks: [],
  organizationId: null,
  isLoadingSites: false,
  isLoadingReport: false,
  isSaving: false,
  isSubmitting: false,
  isUploadingEvidence: false,
  isLoadingHistory: false,
  isLoadingWeeks: false,
  notice: null,

  clearNotice: () => set({ notice: null }),

  loadPreferences: () => {
    const { selectedSiteId, selectedWeekStart } = loadStoredPreferences();
    set({
      ...(selectedWeekStart ? { selectedWeekStart } : {}),
      ...(selectedSiteId ? { selectedSiteId } : {}),
    });
  },

  savePreferences: () => {
    const { selectedSiteId, selectedWeekStart } = get();
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ selectedSiteId, selectedWeekStart }));
  },

  resetForOrganizationChange: () => {
    set({
      sites: [],
      selectedSiteId: null,
      selectedWeekStart: defaultWeekStartIso(),
      draft: emptyDraft(),
      exposure: emptyExposure(),
      report: null,
      evidence: [],
      history: [],
      weeks: [],
      organizationId: null,
      isLoadingReport: false,
      notice: null,
    });
  },

  setSelectedSiteId: (siteId) => {
    set({ selectedSiteId: siteId, weeks: [] });
    get().savePreferences();
    void get().loadReport();
  },

  setSelectedWeekStart: (weekStart) => {
    set({ selectedWeekStart: weekStart });
    get().savePreferences();
    void get().loadReport();
  },

  updateDraftRow: (termKey, field, value) => {
    set((state) => ({
      draft: {
        ...state.draft,
        [termKey]: {
          ...state.draft[termKey],
          [field]: value,
        },
      },
    }));
  },

  updateExposure: (field, value) => {
    set((state) => ({ exposure: { ...state.exposure, [field]: value } }));
  },

  loadReport: async () => {
    const { organizationId, selectedSiteId, selectedWeekStart } = get();
    if (!organizationId || !selectedSiteId) {
      set({ draft: emptyDraft(), exposure: emptyExposure(), report: null, evidence: [], isLoadingReport: false });
      return;
    }
    set({ isLoadingReport: true, history: [] });
    try {
      const response = await getHseWeeklyReport(organizationId, selectedSiteId, selectedWeekStart);
      const data = response as GenericResponse<HseWeeklyReport>;
      // Only apply the answer for the week/branch still selected.
      if (get().selectedSiteId !== selectedSiteId || get().selectedWeekStart !== selectedWeekStart) return;
      set({
        report: data.result ?? null,
        draft: data.result ? draftFromReport(data.result) : emptyDraft(),
        exposure: data.result ? exposureFromReport(data.result) : emptyExposure(),
        isLoadingReport: false,
      });
      void get().loadEvidence();
    } catch (error) {
      set({
        draft: emptyDraft(),
        exposure: emptyExposure(),
        report: null,
        evidence: [],
        isLoadingReport: false,
        notice: failure(error, 'hse.messages.loadFailed'),
      });
    }
  },

  fetchSites: async (organizationId) => {
    set({ isLoadingSites: true, notice: null, organizationId });
    try {
      const response = await getCollectionSites({ organization_id: organizationId });
      const data = response as GenericResponse<CollectionSiteSchema[]>;
      const sites = sortSites(data.result ?? []);
      const prefs = loadStoredPreferences();
      const preferredId = prefs.selectedSiteId;
      const validPreferred = preferredId && sites.some((s) => s.id === preferredId) ? preferredId : null;
      const nextSelected = validPreferred ?? (sites[0]?.id ?? null);
      const nextWeekStart = prefs.selectedWeekStart ?? defaultWeekStartIso();

      set({
        sites,
        selectedSiteId: nextSelected,
        selectedWeekStart: nextWeekStart,
        isLoadingSites: false,
      });
      get().savePreferences();
      await get().loadReport();
    } catch (error) {
      set({
        sites: [],
        selectedSiteId: null,
        isLoadingSites: false,
        notice: failure(error, 'hse.messages.branchesFailed'),
      });
    }
  },

  saveReport: async () => {
    const { organizationId, selectedSiteId, selectedWeekStart, draft, exposure } = get();
    if (!organizationId || !selectedSiteId) {
      set({ notice: { tone: 'error', key: 'hse.messages.selectBranchFirst' } });
      return false;
    }

    const rows: HseWeeklyRow[] = [];
    for (const term of HSE_TERMS) {
      const row = draft[term.key];
      const notes = row.notes.trim();
      if (notes.length > NOTES_MAX) {
        set({ notice: { tone: 'error', key: 'hse.messages.notesTooLong', params: { term: term.key, max: NOTES_MAX } } });
        return false;
      }
      const parsed = parseWholeNumber(row.weeklyCount);
      if (parsed.invalid) {
        set({ notice: { tone: 'error', key: 'hse.messages.countInvalid', params: { term: term.key } } });
        return false;
      }
      rows.push({ term_key: term.key, notes, weekly_count: parsed.value ?? 0 });
    }
    const hours = parseOptionalNumber(exposure.hoursWorked, { integer: false });
    const headcount = parseOptionalNumber(exposure.headcount, { integer: true });
    if (hours.invalid || headcount.invalid) {
      set({ notice: { tone: 'error', key: 'hse.messages.exposureInvalid' } });
      return false;
    }

    set({ isSaving: true, notice: null });
    try {
      const response = await saveHseWeeklyReport(organizationId, selectedSiteId, selectedWeekStart, {
        rows,
        hours_worked: hours.value,
        headcount: headcount.value,
      });
      const data = response as GenericResponse<HseWeeklyReport>;
      set({
        report: data.result ?? get().report,
        draft: data.result ? draftFromReport(data.result) : get().draft,
        exposure: data.result ? exposureFromReport(data.result) : get().exposure,
        isSaving: false,
        notice: { tone: 'success', key: 'hse.saved' },
      });
      return true;
    } catch (error) {
      set({ isSaving: false, notice: failure(error, 'hse.saveFailed') });
      return false;
    }
  },

  submitWeek: async () => {
    const { organizationId, selectedSiteId, selectedWeekStart } = get();
    if (!organizationId || !selectedSiteId) return false;
    // Save what is on screen first so the submitted week is exactly what was reviewed.
    if (!(await get().saveReport())) return false;
    set({ isSubmitting: true, notice: null });
    try {
      const response = await submitHseWeeklyReport(organizationId, selectedSiteId, selectedWeekStart);
      const data = response as GenericResponse<HseWeeklyReport>;
      set({ report: data.result ?? get().report, isSubmitting: false, notice: { tone: 'success', key: 'hse.messages.submitted' } });
      return true;
    } catch (error) {
      set({ isSubmitting: false, notice: failure(error, 'hse.messages.submitFailed') });
      return false;
    }
  },

  reopenWeek: async (reason) => {
    const { organizationId, selectedSiteId, selectedWeekStart } = get();
    if (!organizationId || !selectedSiteId) return false;
    set({ isSubmitting: true, notice: null });
    try {
      const response = await reopenHseWeeklyReport(organizationId, selectedSiteId, selectedWeekStart, reason);
      const data = response as GenericResponse<HseWeeklyReport>;
      set({ report: data.result ?? get().report, isSubmitting: false, notice: { tone: 'success', key: 'hse.messages.reopened' } });
      return true;
    } catch (error) {
      set({ isSubmitting: false, notice: failure(error, 'hse.messages.reopenFailed') });
      return false;
    }
  },

  loadEvidence: async () => {
    const { organizationId, selectedSiteId, selectedWeekStart } = get();
    if (!organizationId || !selectedSiteId) return;
    try {
      const response = await listHseEvidence(organizationId, selectedSiteId, selectedWeekStart);
      const data = response as GenericResponse<HseEvidenceFile[]>;
      if (get().selectedSiteId !== selectedSiteId || get().selectedWeekStart !== selectedWeekStart) return;
      set({ evidence: data.result ?? [] });
    } catch {
      set({ evidence: [] });
    }
  },

  uploadEvidence: async (files, termKey) => {
    const { organizationId, selectedSiteId, selectedWeekStart } = get();
    if (!organizationId || !selectedSiteId || files.length === 0) return false;
    const unsupported = files.find((file) => !getAllowedExtension(file.name));
    if (unsupported) {
      set({ notice: { tone: 'error', key: 'hse.evidence.unsupported', params: { name: unsupported.name } } });
      return false;
    }
    set({ isUploadingEvidence: true, notice: null });
    try {
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const ext = getAllowedExtension(file.name);
          if (!ext) throw new Error(file.name);
          const presigned = parseUtilityProofPresignedResponse(
            await generatePresignedUploadUrl(file.name, ext, 'hse-evidence'),
          );
          await uploadFileToPresignedUrl(file, presigned.upload_url);
          return presigned.id;
        }),
      );
      await addHseEvidence(
        organizationId,
        selectedSiteId,
        selectedWeekStart,
        uploaded.map((asset_id) => ({ asset_id, term_key: termKey })),
      );
      set({ isUploadingEvidence: false, notice: { tone: 'success', key: 'hse.evidence.uploaded', params: { count: files.length } } });
      await Promise.all([get().loadEvidence(), get().loadReport()]);
      return true;
    } catch (error) {
      set({ isUploadingEvidence: false, notice: failure(error, 'hse.evidence.uploadFailed') });
      return false;
    }
  },

  removeEvidence: async (evidenceId) => {
    const { organizationId, selectedSiteId } = get();
    if (!organizationId || !selectedSiteId) return false;
    try {
      await removeHseEvidence(organizationId, selectedSiteId, evidenceId);
      set((state) => ({
        evidence: state.evidence.filter((f) => f.id !== evidenceId),
        notice: { tone: 'success', key: 'hse.evidence.removed' },
      }));
      return true;
    } catch (error) {
      set({ notice: failure(error, 'hse.evidence.removeFailed') });
      return false;
    }
  },

  loadHistory: async () => {
    const { organizationId, selectedSiteId, selectedWeekStart } = get();
    if (!organizationId || !selectedSiteId) return;
    set({ isLoadingHistory: true });
    try {
      const response = await getHseWeeklyHistory(organizationId, selectedSiteId, selectedWeekStart);
      const data = response as GenericResponse<HseHistoryEvent[]>;
      set({ history: data.result ?? [], isLoadingHistory: false });
    } catch (error) {
      set({ history: [], isLoadingHistory: false, notice: failure(error, 'hse.history.loadFailed') });
    }
  },

  loadWeeks: async () => {
    const { organizationId, selectedSiteId } = get();
    if (!organizationId || !selectedSiteId) return;
    set({ isLoadingWeeks: true });
    try {
      const response = await listHseWeeks(organizationId, selectedSiteId);
      const data = response as GenericResponse<HseWeekSummary[]>;
      set({ weeks: data.result ?? [], isLoadingWeeks: false });
    } catch (error) {
      set({ weeks: [], isLoadingWeeks: false, notice: failure(error, 'hse.weeks.loadFailed') });
    }
  },
}));

export const isWeekSubmitted = (status: HseReportStatus | undefined | null): boolean => status === 'submitted';

export { NOTES_MAX };
